/**
 * H5 — the handoff behind the idle Stage's "Register from Details" CTA.
 *
 * `stage/TournamentIdle` is CORE-only (G5b: it may never call a `useQuery`,
 * since `Stage` mounts it for every live game, not only a tournament's) and
 * so cannot know whether this tournament is a free join or a paid register
 * until the REST response lands — which only `DetailsPanel` (inside the
 * query-safe `panels/` lazy island) has. So the idle card cannot press the
 * right CTA itself; it only switches the rail to the Details tab and arms
 * this flag, and `DetailsPanel`'s model consumes it ONCE — the next time its
 * own `cta.kind` has resolved away from `'none'` — pressing that CTA exactly
 * as if the user had tapped it there themselves.
 *
 * SCOPED BY TOURNAMENT ID (H-review): a bare boolean would let an arm on
 * tournament A be consumed by tournament B's Details tab if the player
 * switches tournaments between the press and the consume (e.g. the idle
 * Stage's own tournament changes while the rail tab is mid-switch) — this
 * stores WHICH tournament armed it and only consumes on an exact match.
 *
 * A plain module-level value, not a store slice: this is a one-shot signal
 * between two units that do not otherwise share state, not game state, and
 * surviving a reload would be wrong (a stale arm from a previous visit must
 * not auto-press anything).
 */
let armedFor: string | null = null;

export function armRegistrationIntent(tournamentId: string): void {
  armedFor = tournamentId;
}

/** consumes the flag ONLY for a matching `tournamentId` — a mismatch (or a
 * second call in the same tick) returns `false` and leaves any OTHER arm
 * untouched. */
export function consumeRegistrationIntent(tournamentId: string): boolean {
  if (armedFor === null || armedFor !== tournamentId) {
    return false;
  }
  armedFor = null;
  return true;
}

/** no argument -> "is anything armed"; a `tournamentId` -> "armed for THIS one". */
export function peekRegistrationIntent(tournamentId?: string): boolean {
  if (tournamentId === undefined) {
    return armedFor !== null;
  }
  return armedFor === tournamentId;
}

/** test-only: clears the flag without consuming a legitimate arm. */
export function resetRegistrationIntentForTests(): void {
  armedFor = null;
}

export default {
  armRegistrationIntent,
  consumeRegistrationIntent,
  peekRegistrationIntent,
};
